import React from 'react';
import { Calendar, Check, CheckCircle, Circle, MessageSquare, Trash2, User } from 'lucide-react';

type JobTask = Record<string, any>;

interface ActionTaskListProps {
  tasks: JobTask[];
  staffList: JobTask[];
  onToggleTask(task: JobTask): void;
  onUpdateTaskDate(taskId: string, date: string): void;
  onUpdateTaskAssignee(taskId: string, assigneeId: string | null): Promise<void>;
  onDeleteTask(taskId: string): Promise<void>;
  onUpdateTaskDescription(taskId: string, description: string): Promise<void>;
}

const PRE_EVENT_PATTERN = /prep(?:aration)?(?:\s+gear)?|pre[-\s]?event|pack[-\s]?in|set[-\s]?up/i;
const POST_EVENT_PATTERN = /return(?:\s*&\s*inspect)?|post[-\s]?event|pack[-\s]?out/i;
const EVENT_DAY_PATTERN = /event[-\s]?day|on[-\s]?site|live\s+(?:event|operation)/i;

export const getOperationalTaskPhase = (task: JobTask): 0 | 1 | 2 => {
  const searchable = `${task.title || ''}\n${task.description || ''}`;
  if (task.task_type === 'AUTO_LOGISTICS' && /prep\s+gear/i.test(task.title || '')) return 0;
  if (task.task_type === 'AUTO_LOGISTICS' && /return\s*&\s*inspect/i.test(task.title || '')) return 2;
  if (POST_EVENT_PATTERN.test(searchable)) return 2;
  if (PRE_EVENT_PATTERN.test(searchable)) return 0;
  if (EVENT_DAY_PATTERN.test(searchable)) return 1;
  return 1;
};

const timestamp = (value: unknown) => {
  const parsed = value ? new Date(String(value)).getTime() : Number.POSITIVE_INFINITY;
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

export const groupTasksForDisplay = (tasks: JobTask[]) => {
  const ordered = tasks
    .map((task, originalIndex) => ({ task, originalIndex }))
    .sort((left, right) => {
      const phaseDifference = getOperationalTaskPhase(left.task) - getOperationalTaskPhase(right.task);
      if (phaseDifference !== 0) return phaseDifference;
      const dueDateDifference = timestamp(left.task.due_date) - timestamp(right.task.due_date);
      if (dueDateDifference !== 0) return dueDateDifference;
      return left.originalIndex - right.originalIndex;
    });

  const phaseLabels = ['Pre-event / preparation', 'Event-day / on-site', 'Post-event / return'] as const;
  const groups = new Map<string, { groupName: string; phase: 0 | 1 | 2; tasks: JobTask[] }>();
  ordered.forEach(({ task }) => {
    let groupName = 'General Project Tasks';
    if (task.task_type === 'AUTO_LOGISTICS' && task.title?.includes(': ')) {
      groupName = task.title.split(': ').slice(1).join(': ');
    } else if (task.title?.includes('TRIAGE')) {
      groupName = 'Management Triage / Gear Issues';
    }
    const phase = getOperationalTaskPhase(task);
    const key = `${phase}:${groupName}`;
    const group = groups.get(key) || { groupName, phase, tasks: [] };
    groups.set(key, { ...group, tasks: [...group.tasks, task] });
  });

  return Array.from(groups.values()).map(group => ({ ...group, phaseLabel: phaseLabels[group.phase] }));
};

export const ActionTaskList: React.FC<ActionTaskListProps> = ({
  tasks,
  staffList,
  onToggleTask,
  onUpdateTaskDate,
  onUpdateTaskAssignee,
  onDeleteTask,
  onUpdateTaskDescription,
}) => {
  if (tasks.length === 0) {
    return <div className="text-center py-12 text-sm text-slate-500 font-medium italic border-2 border-dashed border-white/10 rounded-[32px]">No tasks active for this project.</div>;
  }

  return (
    <div className="space-y-8" data-testid="action-task-groups">
      {groupTasksForDisplay(tasks).map(({ groupName, phase, phaseLabel, tasks: groupedTasks }) => (
        <section key={`${phase}:${groupName}`} className="min-w-0 space-y-3" data-testid="action-task-group">
          <h5 className="flex w-full min-w-0 flex-col gap-1 border-b border-brand-neonBlue/20 pb-2 mb-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
            <span className="type-heading break-words text-sm text-brand-neonBlue">Tasks for: {groupName}</span>
            <span className="type-label break-words text-slate-500 sm:shrink-0">{phaseLabel}</span>
          </h5>
          {groupedTasks.map((task: JobTask) => {
            const isDone = task.status === 'COMPLETED';
            return (
              <article
                key={task.id}
                className={`min-w-0 rounded-xl border p-3 transition-all duration-300 sm:p-4 ${isDone ? 'bg-slate-950/80 border-white/5 opacity-60' : 'bg-slate-900/40 border-white/10'}`}
                data-testid="action-task-card"
              >
                <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                  <button
                    className="flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-brand-neonPink"
                    onClick={(event) => { event.stopPropagation(); onToggleTask(task); }}
                    aria-label={isDone ? `Mark ${task.title} incomplete` : `Mark ${task.title} complete`}
                  >
                    {isDone ? <CheckCircle size={26} className="text-green-500 rounded-full" /> : <Circle size={26} className="text-slate-400 hover:text-brand-neonPink transition-colors rounded-full" />}
                  </button>

                  <div className="min-w-0 flex-1">
                    <p className={`break-words text-sm font-bold leading-snug transition-all md:text-base ${isDone ? 'text-slate-500 line-through' : 'text-white'}`}>
                      {task.title}
                    </p>

                    <div className="mt-2 flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
                      <div className={`relative flex min-h-11 w-full items-center gap-1.5 sm:min-h-0 sm:w-fit ${isDone ? 'text-slate-600' : 'text-brand-neonPink hover:text-white'} transition-colors`} onClick={(event) => event.stopPropagation()}>
                        <Calendar size={14} className="shrink-0" />
                        <span className="type-label">Due:</span>
                        <input
                          type="date"
                          disabled={isDone}
                          value={task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : ''}
                          onChange={(event) => onUpdateTaskDate(task.id, event.target.value)}
                          className={`type-numeric relative z-10 min-w-0 flex-1 cursor-pointer bg-transparent text-xs outline-none transition-colors sm:flex-none ${isDone ? 'text-slate-600' : 'text-brand-neonPink hover:text-white'} [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0`}
                          title={isDone ? '' : 'Click to change due date'}
                        />
                      </div>

                      <div className={`flex min-h-11 min-w-0 w-full items-center gap-1.5 border-t border-white/10 pt-2 sm:min-h-0 sm:w-auto sm:flex-1 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0 ${isDone ? 'opacity-50 pointer-events-none' : ''}`}>
                        <User size={14} className={task.assignee_id ? 'text-brand-neonBlue shrink-0' : 'text-slate-600 shrink-0'} />
                        <select
                          value={task.assignee_id || ''}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => onUpdateTaskAssignee(task.id, event.target.value || null)}
                          className={`min-w-0 w-full cursor-pointer truncate bg-transparent text-xs font-semibold outline-none transition-colors ${task.assignee_id ? 'text-brand-neonBlue' : 'text-slate-500 hover:text-white'}`}
                        >
                          <option value="">Unassigned</option>
                          {staffList.map((staff: JobTask) => <option key={staff.id} value={staff.id}>{staff.full_name || staff.name}</option>)}
                        </select>
                      </div>

                      <button
                        onClick={(event) => { event.stopPropagation(); onDeleteTask(task.id); }}
                        className="ml-auto flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-red-500/10 hover:text-red-500"
                        title="Delete Task"
                        aria-label={`Delete ${task.title}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {task.description && (
                  <div className="min-w-0 pt-3 sm:pl-[60px] sm:pr-4" data-testid="action-task-description">
                    <div className="min-w-0 rounded-xl border border-white/5 bg-black/40 p-3 animate-fade-in sm:p-4">
                      <div className="min-w-0 space-y-2">
                        {task.description.split('\n').map((line: string, lineIndex: number) => {
                          const isUnchecked = line.startsWith('• ');
                          const isChecked = line.startsWith('✓ ');
                          if (isUnchecked || isChecked) {
                            const cleanText = line.substring(2);
                            return (
                              <button
                                type="button"
                                key={lineIndex}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  const allLines = task.description.split('\n');
                                  allLines[lineIndex] = isChecked ? `• ${cleanText}` : `✓ ${cleanText}`;
                                  onUpdateTaskDescription(task.id, allLines.join('\n'));
                                }}
                                className={`flex min-h-11 w-full min-w-0 items-center gap-3 rounded-xl p-3 text-left transition-all ${isChecked ? 'bg-brand-neonBlue/10 opacity-50' : 'hover:bg-white/5 bg-slate-900/50 border border-white/5'}`}
                              >
                                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${isChecked ? 'bg-brand-neonBlue border-brand-neonBlue text-black shadow-[0_0_10px_rgba(34,211,238,0.5)]' : 'border-slate-500 text-transparent'}`}><Check size={12} /></span>
                                <span className={`min-w-0 break-words text-sm leading-snug transition-all ${isChecked ? 'line-through text-slate-500' : 'text-slate-300'}`}>{cleanText}</span>
                              </button>
                            );
                          }
                          if (line.trim() === '') return null;
                          return <p key={lineIndex} className="type-label mb-1 mt-4 break-words text-slate-400 first:mt-0">{line}</p>;
                        })}

                        {task.fieldCrewNotes && (
                          <div className="mt-6 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4 shadow-[0_0_15px_rgba(234,179,8,0.05)] animate-fade-in">
                            <h4 className="type-heading mb-2 flex items-center gap-2 text-sm text-yellow-500"><MessageSquare size={14} /> Field Crew Notes</h4>
                            <p className="break-words whitespace-pre-wrap text-sm leading-relaxed text-yellow-100/90">&quot;{task.fieldCrewNotes}&quot;</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ))}
    </div>
  );
};
